/** The bump-arcade verb set:
 *  - `rule.bumpFromBelow` (global): a `head-bump` on a bumpable surface
 *    knocks whatever stands on it near the bumper (`hit-from-below`) and pops
 *    partner players standing there;
 *  - `rule.vulnerableWindow` (enemy): hits enrage (multi-hit kinds) then flip
 *    it for a window; touching a flipped one by a kicker defeats it; ignored,
 *    it recovers faster;
 *  - `rule.kicker` (player): chain scoring + the kick pose;
 *  - `rule.powBlock` (block): limited-use stage-wide hit, refills on a signal. */
import { bool, id, int, list, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import { standingLevelBelow, surfaces } from '../physics.js';
import type { JsonObject } from '../../json.js';
import { addTag, deltaX, overlaps, removeTag, type EntityV1 } from '../world.js';

export type BumpConfig = { bumperTag: string; targetTags: string[]; reach: number; partnerPopSpeed: number };

export const bumpFromBelow = defineModule<BumpConfig>({
  id: 'rule.bumpFromBelow', version: 1, attach: 'global', role: 'rule',
  summary: 'Bumping a surface from below hits entities standing on it near the bumper and pops partner players.',
  config: obj({ bumperTag: id(), targetTags: list(id(), 6, 1), reach: num(0.1, 6), partnerPopSpeed: num(0, 30) }),
  requires: ['body.platformer', 'body.surface'], events: ['bump'], presentation: { bump: { cue: 'impact' } },
  proofs: ['bump-hits-standing-target-in-reach', 'out-of-reach-untouched', 'airborne-untouched', 'non-bumpable-ignored', 'partner-popped'],
  animStates: () => [],
  systems: {
    interact(ctx) {
      const c = ctx.config;
      for (const s of ctx.signals('head-bump')) {
        const surface = ctx.entity(s.target), bumper = ctx.entity(s.source);
        if (!surface || !bumper || !bumper.tags.includes(c.bumperTag)) continue;
        if (ctx.peerConfig(surface, 'body.surface')?.bumpable !== true) continue;
        ctx.react(surface, 'squash');
        ctx.emit('bump', { x: bumper.x, y: surface.y, slot: bumper.slot ?? 0 });
        for (const e of ctx.entities({ status: 'active' })) {
          if (e.id === bumper.id || e.groundId !== surface.id || !e.grounded) continue;
          if (Math.abs(deltaX(ctx.world, bumper.x, e.x)) > c.reach) continue;
          if (c.targetTags.some(t => e.tags.includes(t))) ctx.signal('hit-from-below', e, bumper, {});
          else if (e.tags.includes(c.bumperTag) && c.partnerPopSpeed > 0) { e.vy = -c.partnerPopSpeed; e.grounded = false; e.groundId = null; }
        }
      }
    },
  },
  solverTargets(ctx, _self, player) {
    // Stand on the level beneath a hittable target, right under it.
    const list = surfaces(ctx);
    const out = [];
    for (const e of ctx.entities({ status: 'active' })) {
      if (!e.grounded || !e.groundId || e.tags.includes('vulnerable') || e.tags.includes('defeated')) continue;
      if (!ctx.config.targetTags.some(t => e.tags.includes(t)) || !e.tags.includes('required')) continue;
      const ground = list.find(s => s.entity.id === e.groundId);
      if (!ground?.bumpable) continue;
      const below = standingLevelBelow(ctx.world, list, e.x, ground.bottom);
      if (below) out.push({ x: e.x, y: below.top, weight: 5, label: 'flip-from-below', press: true });
    }
    void player;
    return out;
  },
});

export type VulnerableConfig = {
  hitsToFlip: number; flipTicks: number; requireGrounded: boolean;
  angrySpeedMul: number; recoverSpeedMul: number; flipScore: number; defeatTicks: number;
  /** Signals that count as a hit (`hit-from-below`, `pow`). */
  hitSignals: string[];
  /** Spawn on defeat (e.g. a coin), unless it was the last required one. */
  dropArchetype?: string;
  dropSpeed: number;
  /** Upward pop when flipped / when it recovers (0: stays on its surface). */
  flipPop: number; recoverPop: number;
  enrageState: boolean;
};

export const vulnerableWindow = defineModule<VulnerableConfig>({
  id: 'rule.vulnerableWindow', version: 1, attach: 'entity', role: 'rule',
  summary: 'Hits (named signals: bumps, POW, a stun broadcast) enrage then flip the entity for a harmless window; a kicker touching it defeats it (without kickers it only recovers); ignored, it recovers faster.',
  config: obj({ hitsToFlip: int(1, 4), flipTicks: int(15, 900), requireGrounded: bool(), angrySpeedMul: num(1, 4), recoverSpeedMul: num(1, 4),
    flipScore: int(0, 100000), defeatTicks: int(1, 120), hitSignals: list(id(), 4, 1),
    dropArchetype: id(), dropSpeed: num(0, 10), flipPop: num(0, 20), recoverPop: num(0, 20), enrageState: bool() }, ['dropArchetype']),
  requires: [], events: ['enrage', 'flip', 'recover', 'defeat'],
  presentation: { enrage: { cue: 'bounce', vfx: { effect: 'flash', palette: 3 } }, flip: { cue: 'impact', vfx: { effect: 'burst', palette: 2, count: 8 } },
    recover: { cue: 'none' }, defeat: { cue: 'destroy', vfx: { effect: 'burst', palette: 'slot', count: 14 } } },
  proofs: ['one-hit-flips', 'two-hits-enrage-then-flip', 'airborne-immune-when-required', 'kick-defeats-flipped', 'recovers-faster', 'drop-unless-last', 'defeated-despawns', 'stun-only-without-kickers'],
  animStates: c => (c.enrageState ? ['hit', 'down'] : ['down']),
  mapArchetypeRefs: (c, rename) => (c.dropArchetype ? { ...c, dropArchetype: rename(c.dropArchetype) } : c),
  check: (c, _ctx, archetype) => (c.hitsToFlip > 1 && !c.enrageState ? [`${archetype}: multi-hit kinds must show an enrage state`] : []),
  init(ctx, e) { Object.assign(ctx.data(e), { hits: 0, state: 'normal', timer: 0, speedMul: 1, angerTimer: 0 }); },
  systems: {
    rules(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      if (d.state === 'defeated') {
        d.timer = read(d, 'timer') - 1;
        if (read(d, 'timer') <= 0) ctx.despawn(e, 'defeated');
        return;
      }
      if (d.state === 'flipped') {
        const kicker = ctx.entities({ status: 'active' }).find(k => ctx.hasModule(k, 'rule.kicker') && overlaps(ctx.world, k, e));
        if (kicker) {
          d.state = 'defeated'; d.timer = c.defeatTicks;
          removeTag(e, 'vulnerable'); addTag(e, 'defeated');
          e.vy = -6; e.grounded = false;
          ctx.signal('kicked', kicker, e, {});
          ctx.signal('defeated', e, kicker, { x: e.x, y: e.y - e.h / 2, tags: [...e.tags] });
          ctx.emit('defeat', { x: e.x, y: e.y - e.h / 2, slot: kicker.slot ?? 0, kind: e.archetype });
          ctx.react(e, 'spin');
          const othersLeft = ctx.entities({ tag: 'required', status: 'any' }).some(o => o.id !== e.id && !o.tags.includes('defeated'));
          if (c.dropArchetype && othersLeft) {
            ctx.spawn(c.dropArchetype, { x: e.x, y: e.y, facing: kicker.facing, params: { dir: kicker.facing } });
          }
          return;
        }
        d.timer = read(d, 'timer') - 1;
        if (read(d, 'timer') <= 45) ctx.react(e, 'flicker');
        if (read(d, 'timer') <= 0) {
          d.state = 'normal'; d.hits = 0; d.speedMul = c.recoverSpeedMul;
          removeTag(e, 'vulnerable'); removeTag(e, 'immobile');
          if (c.recoverPop > 0) { e.vy = -c.recoverPop; e.grounded = false; }
          ctx.emit('recover', { x: e.x, y: e.y - e.h / 2 });
        }
        return;
      }
      const hit = ctx.world.signals.find(s => s.target === e.id && (c.hitSignals as readonly string[]).includes(s.kind));
      if (!hit) { if (read(d, 'angerTimer') > 0) d.angerTimer = read(d, 'angerTimer') - 1; return; }
      if (c.requireGrounded && !e.grounded) return;
      const bumper = ctx.entity(hit.source);
      d.hits = read(d, 'hits') + 1;
      if (read(d, 'hits') < c.hitsToFlip) {
        d.speedMul = read(d, 'speedMul') * c.angrySpeedMul; d.angerTimer = 12;
        ctx.emit('enrage', { x: e.x, y: e.y - e.h / 2, slot: bumper?.slot ?? 0, kind: e.archetype });
        return;
      }
      d.state = 'flipped'; d.timer = c.flipTicks;
      addTag(e, 'vulnerable'); addTag(e, 'immobile');
      if (c.flipPop > 0) { e.vy = -c.flipPop; e.grounded = false; }
      if (bumper?.slot !== null && bumper?.slot !== undefined) ctx.addScore(bumper.slot, c.flipScore);
      ctx.emit('flip', { x: e.x, y: e.y - e.h / 2, slot: bumper?.slot ?? 0, points: c.flipScore, kind: e.archetype });
    },
    present(ctx, e) {
      if (!e || e.status === 'hidden') return;
      const d = ctx.data(e);
      if (d.state === 'flipped' || d.state === 'defeated') ctx.setAnim(e, 'down');
      else if (ctx.config.enrageState && read(d, 'angerTimer') > 0) ctx.setAnim(e, 'hit');
    },
  },
  solverTargets(ctx, e) {
    if (!e || ctx.data(e).state !== 'flipped' || ctx.archetypesWith('rule.kicker').length === 0) return [];
    return [{ x: e.x, y: e.y, weight: 9, label: 'kick' }];
  },
});

export type KickerConfig = { chainScores: number[]; chainTicks: number; poseTicks: number };

export const kicker = defineModule<KickerConfig>({
  id: 'rule.kicker', version: 1, attach: 'entity', role: 'rule',
  summary: 'Defeats flipped entities on touch; consecutive kicks inside a window score an escalating chain; shows the kick pose.',
  config: obj({ chainScores: list(int(0, 100000), 8, 1), chainTicks: int(1, 600), poseTicks: int(1, 60) }),
  requires: ['rule.vulnerableWindow'], events: ['kick'], presentation: { kick: { cue: 'score' } },
  proofs: ['chain-escalates-in-window', 'chain-resets-after-window', 'kick-pose'],
  animStates: () => ['action-1'],
  hud: () => [{ id: 'chain', label: 'CHAIN', kind: 'counter' }],
  // Per seat: each player's own chain.
  hudValues(ctx) {
    const kickers = ctx.entities({ status: 'any' }).filter(e => ctx.hasModule(e, 'rule.kicker') && e.slot !== null);
    return { chain: Array.from({ length: ctx.world.playerCount }, (_, slot) => {
      const player = kickers.find(e => e.slot === slot);
      return player ? read(ctx.data(player), 'chain') : 0;
    }) };
  },
  init(ctx, e) { Object.assign(ctx.data(e), { chain: 0, window: 0, pose: 0 }); },
  systems: {
    flow(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (read(d, 'window') > 0) { d.window = read(d, 'window') - 1; if (read(d, 'window') === 0) d.chain = 0; }
      if (read(d, 'pose') > 0) d.pose = read(d, 'pose') - 1;
      for (const s of ctx.signals('kicked')) {
        if (s.target !== e.id) continue;
        const chain = Math.min(c.chainScores.length, read(d, 'chain') + 1);
        const points = c.chainScores[chain - 1]!;
        d.chain = chain; d.window = c.chainTicks; d.pose = c.poseTicks;
        if (e.slot !== null) ctx.addScore(e.slot, points);
        const victim = ctx.entity(s.source);
        ctx.emit('kick', { x: victim?.x ?? e.x, y: (victim?.y ?? e.y) - 0.4, slot: e.slot ?? 0, points, chain });
      }
    },
    present(ctx, e) {
      if (e && e.status === 'active' && read(ctx.data(e), 'pose') > 0) ctx.setAnim(e, 'action-1');
    },
  },
});

export type PowConfig = { uses: number; targetTag: string; clearTag: string; refillOn: 'bonus-cleared' | 'never'; shrink: boolean };

export const powBlock = defineModule<PowConfig>({
  id: 'rule.powBlock', version: 1, attach: 'entity', role: 'rule',
  summary: 'A block struck from below a limited number of times: each strike hits every grounded target and clears hazards; it shrinks, vanishes when spent and may refill.',
  config: obj({ uses: int(1, 9), targetTag: id(), clearTag: id(), refillOn: oneOf('bonus-cleared', 'never'), shrink: bool() }),
  requires: ['body.surface', 'body.platformer'], events: ['pow'], presentation: { pow: { cue: 'impact', vfx: { effect: 'shake' } } },
  proofs: ['strike-hits-all-grounded', 'airborne-spared', 'uses-deplete-then-vanish', 'refills-on-signal', 'clears-hazards'],
  includeHidden: true,
  animStates: () => [],
  hud: () => [{ id: 'uses', label: 'POW', kind: 'pips' }],
  hudValues(ctx) {
    const block = ctx.entities({ status: 'any' }).find(e => ctx.hasModule(e, 'rule.powBlock'));
    return { uses: block ? read(ctx.data(block), 'uses') : 0 };
  },
  init(ctx, e) { Object.assign(ctx.data(e), { uses: ctx.config.uses, baseH: e.h }); },
  systems: {
    interact(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      const strike = ctx.signals('head-bump').find(s => s.target === e.id);
      if (!strike || read(d, 'uses') <= 0) return;
      const bumper = ctx.entity(strike.source);
      d.uses = read(d, 'uses') - 1;
      ctx.react(e, 'squash');
      ctx.emit('pow', { x: e.x, y: e.y - e.h / 2, slot: bumper?.slot ?? 0, uses: read(d, 'uses') });
      for (const t of ctx.entities({ tag: c.targetTag, status: 'active' })) if (t.grounded) ctx.signal('pow', t, bumper ?? null, {});
      for (const h of ctx.entities({ tag: c.clearTag, status: 'any' })) ctx.despawn(h, 'pow');
      resize(e, d, c);
    },
    flow(ctx, e) {
      if (!e) return;
      const d = ctx.data(e), c = ctx.config;
      if (c.refillOn === 'bonus-cleared' && ctx.signals('bonus-cleared').length) { d.uses = c.uses; resize(e, d, c); }
    },
  },
  solverTargets(ctx, e) {
    if (!e || e.status !== 'active' || read(ctx.data(e), 'uses') <= 0) return [];
    const grounded = ctx.entities({ tag: ctx.config.targetTag, status: 'active' })
      .filter(t => t.grounded && !t.tags.includes('vulnerable') && !t.tags.includes('defeated')).length;
    if (grounded < 2) return [];
    const below = standingLevelBelow(ctx.world, surfaces(ctx), e.x, e.y);
    return below ? [{ x: e.x, y: below.top, weight: 4 + 4 * grounded, label: 'pow', press: true }] : [];
  },
});

function resize(e: EntityV1, d: JsonObject, c: PowConfig): void {
  const uses = read(d, 'uses'), base = read(d, 'baseH');
  if (c.shrink) e.h = base * (0.5 + 0.5 * (uses / c.uses));
  e.status = uses > 0 ? 'active' : 'hidden';
}
